import type {
	ConnectionFieldFilterEnumValue,
	FilterGroup,
} from '@atlassian/jira-polaris-domain-view/src/filter/types.tsx';

import type { View } from '@atlassian/jira-polaris-domain-view/src/view/types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';
import { getCurrentViewFilterGroups } from '../../selectors/filters.tsx';
import { isCurrentViewAutosaveEnabled } from '../../selectors/view/autosave/index.tsx';
import type { State, Props } from '../../types.tsx';
import { saveViewWithAutoSave } from '../save/index.tsx';
import { fireViewUpdatedEvent } from '../utils/analytics.tsx';
import { updateViewState } from '../utils/state/index.tsx';
import { currentViewFilter } from '../utils/views/index.tsx';
import {
	createGroupFilterFromIssueTypeName,
	getIssueTypeNameFromGroupFilter,
} from '../../utils/filter-groups.tsx';

const getUpdatedGroups = (
	groups: FilterGroup[],
	issueTypeName: string,
	options: ConnectionFieldFilterEnumValue[],
) => {
	let updatedGroups = [...groups];

	if (
		!updatedGroups.find(
			(group) =>
				group.groupFilter && getIssueTypeNameFromGroupFilter(group.groupFilter) === issueTypeName,
		)
	) {
		updatedGroups.push({
			groupFilter: createGroupFilterFromIssueTypeName(issueTypeName),
			filters: [],
			filterEnums: options,
		});
	} else {
		updatedGroups = updatedGroups.map((group) => {
			if (
				group.groupFilter &&
				getIssueTypeNameFromGroupFilter(group.groupFilter) === issueTypeName
			) {
				return { ...group, filterEnums: options };
			}
			return group;
		});
	}

	return updatedGroups.filter(
		({ filters, filterEnums }) => filters.length > 0 || (filterEnums && filterEnums.length > 0),
	);
};

export const updateConnectionsMatchingGroupOrColumnFilterGroup =
	(
		options: ConnectionFieldFilterEnumValue[],
		issueTypeName: string,
		onSuccess?: () => void,
		onError?: (error: Error) => void,
	): Action<State, Props> =>
	async ({ getState, setState, dispatch }, props) => {
		const state = getState();
		const isAutosaveEnabled = isCurrentViewAutosaveEnabled(state, props);

		const currentViewFilterGroups = getCurrentViewFilterGroups(state, props);
		const updatedGroups = getUpdatedGroups(currentViewFilterGroups, issueTypeName, options);

		const viewMutation = (view: View): View => {
			return {
				...view,
				filterGroups: isAutosaveEnabled ? updatedGroups : view.filterGroups,
				modified: isAutosaveEnabled,
				draft: {
					...view.draft,
					filterGroups: updatedGroups,
				},
			};
		};

		const { changedView, viewSets } = updateViewState(
			getState().viewSets,
			currentViewFilter(props.currentViewSlug),
			viewMutation,
		);

		if (changedView) {
			setState({ viewSets });

			dispatch(
				saveViewWithAutoSave(changedView.id, (view) => {
					if (view === undefined) {
						return;
					}

					if (view.saveError === undefined) {
						return onSuccess?.();
					}

					logSafeErrorWithoutCustomerDataWrapper(
						'polaris.error.controllers.views.actions.filter.update-connections-matching-filter',
						'Failed to update connections matching group or column filter',
						view.saveError,
					);

					return onError?.(view.saveError);
				}),
			);

			fireViewUpdatedEvent(props.createAnalyticsEvent, changedView, {
				updatedItems: [{ name: 'filter' }],
			});
		}
	};
